// SecurityPrivacyComplianceDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// (Bootstrap + React Router v4/v5)
// Style: matches InterviewGamePlanDetail.jsx / PerformanceMathSizingDetail.jsx / StorageDatabasesDetail.jsx
// Includes: Template + Example cards with right-side badges, plus “Say” callouts.
// Example used in every section: Facebook-like Home Feed.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function LabeledCard({ label, tone, title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Facebook-like Home Feed — example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

function CodeCard({ title = "Template — code / snippet", children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone="secondary">Template</BadgeRight>
        </div>
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/* ================== TOC ================== */
const TOC = [
  { id: "auth", label: "A) AuthN/AuthZ & service auth" },
  { id: "secrets", label: "B) Secrets mgmt & rotation" },
  { id: "encryption", label: "C) Encryption & envelope" },
  { id: "pii", label: "D) PII, minimization, RTBF" },
  { id: "abuse", label: "E) Abuse prevention & WAF" },
  { id: "compliance", label: "F) Compliance: GDPR/CCPA/PCI" },
  { id: "ops-guardrails", label: "G) Operational guardrails" },
  { id: "checklists", label: "H) Quick checklists" },
  { id: "snippets", label: "I) Sample snippets" },
  { id: "one-liners", label: "J) One-liners" },
];

/* ================== page ================== */
export default function SecurityPrivacyComplianceDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id: sectionId }) => {
      const el = document.getElementById(sectionId);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  const feed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      services: ["API Gateway", "Feed Service", "Graph/Relationship Service", "Auth/IdP", "Moderation"],
      sensitiveData: ["email", "phone", "IP address", "device_id", "location", "content reports"],
      endpoints: {
        getFeed: "GET /v1/feed",
        post: "POST /v1/posts",
        like: "POST /v1/posts/{post_id}/likes",
        report: "POST /v1/content/{content_id}/reports",
      },
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Security, privacy, compliance</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(25,135,84,.07), rgba(13,110,253,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">Security, privacy, compliance — detailed guide</h1>
          <p className="text-muted mb-0">
            Practical AuthN/AuthZ, secrets, encryption, PII/retention, abuse prevention, and
            compliance patterns — with a {feed.name} example in every section.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none order-xl-1">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id: sectionId, label }) => (
                    <a
                      key={sectionId}
                      href={`#${sectionId}`}
                      className={`nav-link px-0 ${activeId === sectionId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === sectionId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">
              Tip: Describe “threats → controls → monitoring” for interviews.
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 order-xl-2">
          {/* A) Auth */}
          <Section id="auth" title="A) AuthN/AuthZ & service-to-service auth">
            <SayCallout>
              “Authentication is identity; authorization is permission. We enforce at the gateway and in the service
              (defense in depth).”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">Core concepts</h3>
            <ul className="mb-0">
              <li>
                <strong>AuthN:</strong> who the caller is (user/app/service).
              </li>
              <li>
                <strong>AuthZ:</strong> what actions are allowed (scope/role/policy + resource checks).
              </li>
              <li>
                <strong>Least privilege:</strong> narrow scopes, short-lived credentials, auditable decisions.
              </li>
            </ul>

            <TemplateCard title="Auth stack — template">
{`Users (web/mobile):
• OIDC (OAuth2 Auth Code + PKCE)
• Short-lived access token (minutes) + rotating refresh token
• Phishing-resistant MFA (WebAuthn/passkeys) for sensitive flows

Services:
• mTLS (service identity) + per-request authZ policy
• Or OAuth2 Client Credentials for service tokens (short TTL)

AuthZ models:
• RBAC (roles) for coarse permissions
• ABAC (attributes like tenant/region/risk)
• ReBAC (relationship/graph: follower, member, owner)

Critical validation:
• Validate iss, aud, exp/nbf (clock skew), signature, and scope/permissions
• Never trust identity headers unless injected by a trusted gateway`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — Auth design`}>
{`• Gateway validates JWT for ${feed.endpoints.getFeed} and write endpoints.
• Feed service re-checks authorization for privacy (blocks, private accounts).
• Internal hops: mTLS identity between API Gateway ↔ Feed Service ↔ Graph Service.
• Step-up auth (WebAuthn) for account recovery or changing email/password.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Long-lived tokens with no revocation plan",
                "Skipping audience (aud) checks or trusting any issuer",
                "Bearer tokens in logs/traces",
                "Using localStorage for tokens in browsers (XSS risk)",
                "No CSRF protection when using cookies for sessions",
              ]}
            />
          </Section>

          {/* In-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Secrets */}
          <Section id="secrets" title="B) Secrets management & rotation">
            <SayCallout>
              “Secrets live in a central manager, delivered just-in-time, and rotated automatically with audit trails.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">What counts as a secret</h3>
            <ul className="mb-0">
              <li>API keys, DB creds, signing keys, private keys, OAuth client secrets, webhook secrets.</li>
              <li>Also: “secret-like” values (connection strings, internal endpoints, salt/pepper).</li>
            </ul>

            <TemplateCard title="Secrets program — template">
{`Storage:
• Use a managed secret store (Vault / AWS Secrets Manager / GCP Secret Manager)
• Encrypt at rest; strict IAM; every access audited

Delivery:
• Do NOT bake into images or repos
• Pull at startup via short-lived identity; or sidecar agent
• Minimize exposure: avoid env var dumps; redact logs

Rotation:
• TLS certificates: ≤ 90 days (automated)
• DB creds: dynamic/leased creds when possible
• API keys: 30–90 days; support key overlap during rotation
• Signing keys: key sets (kid) + rollout/rollback plan

Break-glass:
• Strongly controlled emergency access (hardware keys), time-bound and audited`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — secrets in practice`}>
{`• API Gateway uses rotated JWT verification keys via JWKS caching.
• Feed/Graph services use dynamic DB creds (leased) and auto-rotated mTLS certs.
• Moderation webhooks use per-integration secrets; rotation supports overlap windows.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Hard-coding a “secret zero” (initial root key) in config",
                "No rotation runbook (keys never change)",
                "Secrets leaking into logs, crash dumps, or analytics",
                "Storing secrets in CI variables without access controls/audit",
              ]}
            />
          </Section>

          {/* C) Encryption */}
          <Section id="encryption" title="C) Encryption in transit & at rest — envelope encryption">
            <SayCallout>
              “TLS everywhere, and for sensitive fields we use envelope encryption so keys can rotate without rewriting
              all data.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">What to encrypt</h3>
            <ul className="mb-0">
              <li>
                <strong>In transit:</strong> TLS 1.2+ at the edge; mTLS internally where appropriate.
              </li>
              <li>
                <strong>At rest:</strong> disk/volume encryption + app-level encryption for high-sensitivity fields.
              </li>
              <li>
                <strong>Backups:</strong> encrypted + immutable storage (WORM) + access logging.
              </li>
            </ul>

            <CodeCard title="Envelope encryption — template (pseudocode)">
{`# CMK (KMS/HSM key) never leaves the service boundary
dek_plain, dek_wrapped = KMS.generate_data_key(CMK)

ciphertext = AEAD.encrypt(plaintext, dek_plain, aad=metadata)

store({
  "ciphertext": ciphertext,
  "dek_wrapped": dek_wrapped,
  "alg": "AES-256-GCM",
  "key_id": "cmk_123"
})

# Rotation: re-wrap DEKs with a new CMK without re-encrypting the data`}
            </CodeCard>

            <ExampleCard title={`${feed.name} — encryption choices`}>
{`• TLS for all external endpoints; mTLS for service-to-service.
• Encrypt sensitive fields like email/phone in user profile with envelope encryption.
• Analytics receives only redacted or tokenized identifiers (no raw PII).`}
            </ExampleCard>

            <Pitfalls
              items={[
                "One global key for everything (blast radius + rotation pain)",
                "Plaintext temp files or debug dumps containing sensitive fields",
                "No audit of decrypt operations",
                "Sending raw PII into logs/analytics pipelines",
              ]}
            />
          </Section>

          {/* D) PII */}
          <Section id="pii" title="D) PII handling: minimization, retention & RTBF (deletion)">
            <SayCallout>
              “Privacy is an engineering workflow: classify → minimize → retain only what’s needed → delete reliably.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">PII lifecycle</h3>
            <ul className="mb-0">
              <li>
                <strong>Classify:</strong> tag fields at ingest (Public / Internal / Sensitive / Restricted).
              </li>
              <li>
                <strong>Minimize:</strong> collect only what is necessary; reduce copies; avoid exporting raw PII.
              </li>
              <li>
                <strong>Retention:</strong> explicit time limits per dataset; automated TTL where possible.
              </li>
              <li>
                <strong>RTBF deletion:</strong> delete in DB + propagate to cache, search, logs, analytics; backups expire
                per policy (and are not “restored into production” without deletion replay).
              </li>
            </ul>

            <TemplateCard title="RTBF deletion pipeline — template">
{`1) Identify subject (user_id / stable identity key)
2) Emit deletion event (versioned): user.delete.requested
3) Fanout workers purge:
   • primary DB rows + indexes
   • caches (tenant-aware keys)
   • search documents
   • analytics tables (if required) or enforce TTL
4) Verify (queries + audit evidence)
5) Backups: expire per retention; if restored, reapply deletion events
6) Produce completion record / certificate`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — PII & deletion`}>
{`• User profile includes: email/phone + privacy controls.
• Logs store request_id and user_id (hashed/tokenized where possible), not raw email/phone.
• Deleting an account purges:
  - profile records
  - feed materialization references
  - search index docs (username)
  - caches + CDN variants
  - moderation/report links per policy`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Forgetting caches and search indexes during deletion",
                "PII in logs/traces, support tickets, or crash dumps",
                "Indefinite retention “just in case”",
                "Treating hashing as anonymization without considering re-identification risk",
              ]}
            />
          </Section>

          {/* E) Abuse */}
          <Section id="abuse" title="E) Abuse prevention: WAF, bot controls, spam/fraud signals">
            <SayCallout>
              “We defend at multiple layers: edge WAF + rate limits, then behavior/risk scoring, then step-up controls.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">Defense layers</h3>
            <ul className="mb-0">
              <li>
                <strong>Edge:</strong> WAF rules, DDoS protection, token-bucket rate limits, geo/IP reputation.
              </li>
              <li>
                <strong>Application:</strong> device fingerprinting (privacy-aware), anomaly detection, velocity rules,
                content filtering.
              </li>
              <li>
                <strong>User safety:</strong> reporting flows, moderation queues, appeals, shadow bans where appropriate.
              </li>
            </ul>

            <TemplateCard title="Abuse controls — template">
{`• Rate limit at gateway (per user/app/IP) with 429 + Retry-After
• Challenge on suspicion: CAPTCHA / proof-of-work / email or phone verification
• Spam & fraud detection: velocity limits, reputation scores, risky geo patterns
• Content safety: ML + rules + human review, plus audit trails and appeals
• Protect writes more than reads; cost-weighted quotas (write=5, read=1)`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — abuse controls`}>
{`• Like/comment endpoints are more strictly rate-limited than ${feed.endpoints.getFeed}.
• Suspicious accounts trigger step-up verification before posting.
• Reports endpoint (${feed.endpoints.report}) has abuse throttles + moderation triage queues.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Relying only on IP blocking (easy to bypass, harms shared networks)",
                "Blanket CAPTCHAs that kill UX",
                "No replay protection for webhooks or write operations",
                "No audit trail for moderation actions",
              ]}
            />
          </Section>

          {/* F) Compliance */}
          <Section id="compliance" title="F) Compliance: GDPR/CCPA, PCI (practical engineering view)">
            <SayCallout>
              “Compliance becomes implementable when you translate it into: data map, retention, DSAR workflows, and
              incident runbooks.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">GDPR / CCPA (operational)</h3>
            <ul className="mb-0">
              <li>DSAR: access/export, correction, deletion (RTBF), portability.</li>
              <li>Vendor risk: DPAs, subprocessors, least-privileged access, audit logs.</li>
              <li>Cross-border/data residency: region pinning + contractual controls.</li>
              <li>Breach readiness: detection + triage + comms runbook.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted mt-3">PCI (payments)</h3>
            <ul className="mb-0">
              <li>Minimize scope: hosted payment fields/pages from a compliant provider.</li>
              <li>Avoid storing PAN/CVV; tokenize and store only last4 + brand when needed.</li>
            </ul>

            <ExampleCard title={`${feed.name} — compliance mapping`}>
{`• GDPR/CCPA: deletion pipeline covers profile, feed caches, search docs, and analytics per policy.
• Vendor DPAs: moderation vendors and analytics platforms are audited and access-controlled.
• If payments exist (subscriptions): use hosted checkout, keep the feed system out of PCI scope.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Assuming legal will ‘handle it’ without engineering workflows",
                "Accidentally storing payment data (PAN/CVV) in logs",
                "No DSAR tooling or runbooks (manual, slow, inconsistent)",
                "Data residency requirements not reflected in routing/storage design",
              ]}
            />
          </Section>

          {/* G) Ops guardrails */}
          <Section id="ops-guardrails" title="G) Operational guardrails (audit, monitoring, incident response)">
            <SayCallout>
              “Security controls need monitoring: audit logs, anomaly alerts, and tested incident response.”
            </SayCallout>

            <TemplateCard title="Security operations — template">
{`Audit logs:
• Auth events (login, MFA changes), privilege changes, key usage (KMS decrypt), policy updates

Detection:
• WAF spikes, unusual auth failures, token anomalies, secret access bursts, impossible travel patterns

Response:
• Break-glass access (hardware keys), rotating credentials, token revocation / session purge
• Incident roles + comms template + postmortem actions with owners & dates`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — monitoring & response`}>
{`• Alerts on abnormal login failures and unusual report/like velocity.
• KMS decrypt spikes trigger investigation (possible data exfil attempt).
• “Disable posting” kill switch for emergencies while keeping ${feed.endpoints.getFeed} available.`}
            </ExampleCard>
          </Section>

          {/* H) Checklists */}
          <Section id="checklists" title="H) Quick checklists (fast interview answers)">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">AuthN/AuthZ</div>
                    <ul className="mb-0 small text-muted">
                      <li>OIDC (PKCE) for users</li>
                      <li>Short-lived tokens + rotation</li>
                      <li>mTLS for services</li>
                      <li>RBAC/ABAC/ReBAC as needed</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">Secrets & keys</div>
                    <ul className="mb-0 small text-muted">
                      <li>Central manager + audit</li>
                      <li>Automated rotation (certs ≤ 90d)</li>
                      <li>Envelope encryption for sensitive fields</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">Privacy</div>
                    <ul className="mb-0 small text-muted">
                      <li>Classify → minimize → retain</li>
                      <li>RTBF pipeline to caches/search/analytics</li>
                      <li>Redact logs/traces</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">Abuse</div>
                    <ul className="mb-0 small text-muted">
                      <li>WAF + token buckets</li>
                      <li>Risk scoring + step-up controls</li>
                      <li>Moderation workflow + audit</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <ExampleCard title={`${feed.name} — checklist summary`}>
{`• Short-lived JWTs at edge; mTLS inside; privacy checks in service.
• Secrets via manager + rotation; envelope encrypt sensitive profile fields.
• Delete reliably across DB + cache + search + analytics + backups policy.
• Rate-limit writes + moderation workflow for reports/spam.`}
                </ExampleCard>
              </div>
            </div>
          </Section>

          {/* I) Snippets */}
          <Section id="snippets" title="I) Sample snippets (copy/paste friendly)">
            <CodeCard title="JWT claims — template (example payload)">
{`{
  "iss": "https://idp.example.com",
  "sub": "user_123",
  "aud": "feed-api",
  "scope": "feed:read posts:write",
  "jti": "9f9d7c3a-0a6e-4e5b-b18e-6c3f25a1e1df",
  "iat": 1731080000,
  "exp": 1731081800
}`}
            </CodeCard>

            <CodeCard title="Cookie flags — template (browser sessions)">
{`Set-Cookie: sid=...; HttpOnly; Secure; SameSite=Lax; Path=/; Max-Age=1800`}
            </CodeCard>

            <CodeCard title="CSP starter — template">
{`Content-Security-Policy:
  default-src 'self';
  script-src 'self' 'nonce-<random>';
  object-src 'none';
  frame-ancestors 'none';
  base-uri 'self';`}
            </CodeCard>

            <CodeCard title="Webhook verification — template">
{`# Verify signature to prevent spoofing/replay
sig = HMAC_SHA256(secret, raw_body)
if !constant_time_equals(sig, request.headers["X-Signature"]):
  reject(401)

# Add replay protection (timestamp + nonce) for high-risk callbacks`}
            </CodeCard>

            <ExampleCard title={`${feed.name} — snippet placement`}>
{`• Webhooks for moderation callbacks use HMAC verification + replay protection.
• JWT validation enforces iss/aud/exp; request_id used for audit correlation.
• CSP protects the web UI against script injection.`}
            </ExampleCard>
          </Section>

          {/* J) One-liners */}
          <Section id="one-liners" title="J) One-liners (ready for interviews)">
            <ul className="mb-0">
              <li>“Least privilege + short-lived tokens; step-up auth for sensitive actions.”</li>
              <li>“Gateway validates identity; services enforce resource-level authorization.”</li>
              <li>“mTLS for service identity; never trust identity headers from untrusted hops.”</li>
              <li>“Envelope encryption: DEK per object, wrapped by CMK; decrypt calls are audited.”</li>
              <li>“Privacy is a pipeline: classify → minimize → retain → delete across DB/cache/search/analytics.”</li>
              <li>“WAF + rate limits at edge; risk scoring and moderation workflow for abuse.”</li>
              <li>“We reduce PCI scope via hosted payments; DSAR/RTBF is operationalized via APIs and runbooks.”</li>
            </ul>
          </Section>

          {/* Optional bottom ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none order-xl-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
